// This module controls the loading and caching of stats for the Shoutouts Leaderboard page/table
import Network from 'lib/api/network';

const userCollectionKey = ({
 orderCol, orderDir, startDate, endDate, userSearchQuery, pageViewKey,
}) => {
  const start = startDate || 'infinite';
  const end = endDate || 'infinite';
  const user = userSearchQuery || 'no_users';
  const pvk = pageViewKey || 'no_key';
  return `${orderCol}-${orderDir}-${start}-${end}-${user}-${pvk}`;
};

const userDataUrl = (state, { page, forExport }) => {
  let base = '/points/wallets';
  if (forExport) base += '/export_data';
  let url = `${base}?order_col=${state.orderCol}&order_dir=${state.orderDir}`;
  if (page) url += `&page=${page}`;
  if (state.pageViewKey) url += `&page_view_key=${state.pageViewKey}`;
  if (state.startDate) url += `&start_date=${state.startDate}`;
  if (state.endDate) url += `&end_date=${state.endDate}`;
  if (state.userSearchQuery) url += `&user_search=${state.userSearchQuery}`;
  if (state.forPeoplePage) url += '&for_people_page=true';
  return url;
};

export default {
  namespaced: true,

  state() {
    return {
      orderCol: null,
      orderDir: null,
      startDate: null,
      endDate: null,
      userSearchQuery: '',
      isFetching: false,
      exportLoading: false,
      users: {},
      pageViewKey: null,
      forPeoplePage: false,
      asyncValuesByUser: {},
      outdatedCollectionCols: {},
    };
  },

  getters: {
    currentUserCollection(state) {
      return state.users[userCollectionKey(state)];
    },
  },

  mutations: {
    addUsers(state, { list, nextPage }) {
      const key = userCollectionKey(state);
      if (state.users[key]) {
        state.users[key].nextPage = nextPage;
        state.users[key].list = state.users[key].list.concat(list);
      } else {
        state.users[key] = { nextPage, list, key };
      }
      state.users[key].isOutdated = false;
      list.forEach((user) => {
        // On the people page, some values are going to be changed from this page itself. Rather than find every
        // instance of the user in every user collection already loaded, store some overrides in here
        if (!state.asyncValuesByUser[user.id]) {
          state.asyncValuesByUser[user.id] = {
            allowance: user.points.allowance,
            isCustom: user.points.is_custom_allowance,
            role: user.shoutouts_role,
            state: user.points.state,
          };
        }
      });
    },

    updateAsyncValuesForUser(state, { id, key, value, markColOutdated }) {
      state.asyncValuesByUser[id][key] = value;
      if (markColOutdated) {
        // Because we're changing the values in this column, any already loaded collections sorting
        // on this column will be sorted incorrectly. Mark them as needing reload as to be accessed
        // in userCollectionFetchRequired
        Object.keys(state.users).forEach((uck) => {
          const keyOrderCol = uck.split('-')[0]; // This assumes the UCK starts with orderCol. Do not change that.
          if (keyOrderCol === key) state.users[uck].isOutdated = true;
        });
      }
    },

    clearUserCollection(state, { uck }) {
      // This will be necessary when some collections have asyncValues that were updated post collection load
      // and are thus ordered differently now
      state.users[uck] = null;
    },
  },

  actions: {
    fetchUsers({ state, commit, dispatch }, { page }) {
      commit('update', { module: 'pointsLeaderboard', key: 'isFetching', value: true }, { root: true });
      return new Promise((resolve) => {
        const url = userDataUrl(state, { page });
        Network.get(url, {
          success: ({ list, nextPage }) => {
            commit('addUsers', { list, nextPage });
            resolve();
          },
          error: () => {
            dispatch('setErrorToast', null, { root: true });
          },
          complete: () => { commit('update', { module: 'pointsLeaderboard', key: 'isFetching', value: false }, { root: true }); },
        });
      });
    },

    exportUsersData({ state, commit, dispatch }) {
      const url = userDataUrl(state, { forExport: true });
      commit('update', { module: 'pointsLeaderboard', key: 'exportLoading', value: true }, { root: true });
      dispatch('csvSender/sendCsv', { endpoint: url }, { root: true }).then(() => {
        commit('update', { module: 'pointsLeaderboard', key: 'exportLoading', value: false }, { root: true });
      });
    },

    toggleSorting({ state, commit, dispatch }, { orderCol, fetchFirstPage }) {
      let orderDir;
      if (orderCol === state.orderCol) {
        // This means we're clicking on the column currently being sorted on. Just flip the orderDir.
        orderDir = state.orderDir === 'desc' ? 'asc' : 'desc';
      } else {
        // This means we're clicking on a diff column. Default to desc orderDir.
        orderDir = 'desc';
      }
      commit('update', { module: 'pointsLeaderboard', key: 'orderCol', value: orderCol }, { root: true });
      commit('update', { module: 'pointsLeaderboard', key: 'orderDir', value: orderDir }, { root: true });
      window.donut.track('Points Leaderboard — Sorted Table', { orderCol, orderDir });
      // If the new collection has no users, fetch 'em
      dispatch('fetchDataIfNecessary', { fetchFirstPage });
    },

    updateDateRange({ commit, dispatch }, { startDate, endDate, fetchFirstPage }) {
      commit('update', { module: 'pointsLeaderboard', key: 'startDate', value: startDate }, { root: true });
      commit('update', { module: 'pointsLeaderboard', key: 'endDate', value: endDate }, { root: true });
      window.donut.track('Points Leaderboard — Updated Date Range', { startDate, endDate });
      dispatch('fetchDataIfNecessary', { fetchFirstPage });
    },

    updateUserSearchQuery({ commit, dispatch }, { userSearchQuery, fetchFirstPage }) {
      commit('update', { module: 'pointsLeaderboard', key: 'userSearchQuery', value: userSearchQuery }, { root: true });
      window.donut.track('Points Leaderboard — Searched For User', { userSearchQuery });
      dispatch('fetchDataIfNecessary', { fetchFirstPage });
    },

    updateWalletState({ commit, dispatch }, { user, value }) {
      const oldState = user.points.state;
      // Optimistically update
      commit('updateAsyncValuesForUser', { id: user.id, key: 'state', value, markColOutdated: true });
      // Make actual API call
      Network.put(`/points/wallets/${user.id}`, { state: value }, {
        success: () => {
          dispatch('setSuccessToast', `Successfully ${value === 'inactive' ? 'de' : ''}activated user.`, { root: true });
        },
        error: () => {
          // Revert change
          dispatch('setErrorToast', `Failed to ${value === 'inactive' ? 'de' : ''}activate user.`, { root: true });
          commit('updateAsyncValuesForUser', { id: user.id, key: 'state', value: oldState, markColOutdated: true });
        },
      });
    },

    fetchDataIfNecessary({ state, getters, commit }, { fetchFirstPage }) {
      // First, check if theres an existing collection that has been marked as outdated (happens when the collection)
      // is sorted on a column that has had it's asyncValues updated. In such a case, set the column null
      if (getters.currentUserCollection?.isOutdated) commit('clearUserCollection', { uck: userCollectionKey(state) });
      // Next, if there's no collection (either ever or post-clear above), fetch it's first page
      if (!getters.currentUserCollection) fetchFirstPage();
    },
  },
};
